/**
 * The public web form intake (tracker item 49): the URLs a company points its
 * website at, the copy-paste HTML snippet, and the checks / body of the hosted
 * form page (/request-service/:key). Pure helpers, no React and no API calls.
 */
import { ServiceRequestTimeOfDay, WebFormRequestInput } from '../api/types';

/** The default API base of the app (createAPIClient uses the same). */
export const DEFAULT_API_BASE = 'http://localhost:5000/api';

/**
 * The absolute endpoint a website form posts to. A relative API base (e.g.
 * "/api" behind a proxy) is resolved against this app's origin, since a
 * relative URL would point at the company's own site once pasted there.
 */
export function webFormEndpointUrl(key: string, apiBase: string, origin: string): string {
  const base = new URL(apiBase.replace(/\/+$/, ''), origin.endsWith('/') ? origin : `${origin}/`).toString().replace(/\/+$/, '');
  return `${base}/public/web-form/${encodeURIComponent(key)}/service-requests`;
}

/** The service list the snippet and hosted page load (same key, same 404 rules). */
export function webFormItemsUrl(endpoint: string): string {
  return endpoint.replace(/\/service-requests$/, '/items');
}

/** The hosted form page of this app for the key (a link the company can share). */
export function webFormHostedUrl(key: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}/request-service/${encodeURIComponent(key)}`;
}

/**
 * A self-contained HTML form + script for the company's website. It loads the
 * offered services, posts JSON (a plain form post would land the visitor on a
 * JSON page), keeps a hidden honeypot field ("website"), and shows the
 * server's message with textContent (never as HTML).
 */
export function webFormSnippet(endpoint: string): string {
  const url = JSON.stringify(endpoint).replace(/</g, '\\u003c');
  return `<!-- Service request form. Requests appear in your CRM inbox under Requests. -->
<form id="crm-request-form">
  <p><label>Your name<br><input name="contactName" required maxlength="200" autocomplete="name"></label></p>
  <p><label>Email<br><input name="contactEmail" type="email" maxlength="320" autocomplete="email"></label></p>
  <p><label>Phone<br><input name="contactPhone" type="tel" maxlength="50" autocomplete="tel"></label></p>
  <p><label>Service address<br><input name="serviceAddress" required maxlength="300" autocomplete="street-address"></label></p>
  <p><label>Service<br><select name="requestedItemId"><option value="">Something else (describe below)</option></select></label></p>
  <p><label>Preferred date<br><input name="preferredDate" type="date"></label></p>
  <p><label>Time of day<br><select name="preferredTimeOfDay"><option value="">No preference</option><option value="MORNING">Morning</option><option value="AFTERNOON">Afternoon</option><option value="EVENING">Evening</option></select></label></p>
  <p><label>What do you need?<br><textarea name="notes" maxlength="2000" rows="4"></textarea></label></p>
  <div style="position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden" aria-hidden="true">
    <label>Leave this empty <input name="website" tabindex="-1" autocomplete="off"></label>
  </div>
  <p><button type="submit">Send request</button></p>
  <p id="crm-request-status" role="status"></p>
</form>
<script>
(function () {
  var endpoint = ${url};
  var form = document.getElementById('crm-request-form');
  var status = document.getElementById('crm-request-status');
  fetch(endpoint.replace(/\\/service-requests$/, '/items'))
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (body) {
      if (!body || !body.data) return;
      body.data.forEach(function (item) {
        var option = document.createElement('option');
        option.value = item.id;
        option.textContent = item.name;
        form.elements.requestedItemId.appendChild(option);
      });
    })
    .catch(function () {});
  form.addEventListener('submit', function (event) {
    event.preventDefault();
    var f = form.elements;
    if (!f.contactEmail.value && !f.contactPhone.value) { status.textContent = 'Please give an email address or a phone number.'; return; }
    var data = {
      contactName: f.contactName.value, contactEmail: f.contactEmail.value, contactPhone: f.contactPhone.value,
      serviceAddress: f.serviceAddress.value, requestedItemId: f.requestedItemId.value,
      preferredTimeOfDay: f.preferredTimeOfDay.value, notes: f.notes.value, website: f.website.value
    };
    if (f.preferredDate.value) data.preferredDates = [f.preferredDate.value];
    var button = form.querySelector('button[type="submit"]');
    button.disabled = true;
    status.textContent = 'Sending...';
    fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (res.ok) { form.reset(); status.textContent = 'Thank you! We received your request and will contact you soon.'; }
        else { status.textContent = (res.body && res.body.message) || 'Sorry, your request could not be sent. Please call us.'; }
      })
      .catch(function () { status.textContent = 'Sorry, your request could not be sent. Please call us.'; })
      .then(function () { button.disabled = false; });
  });
})();
</script>`;
}

/** The hosted form's fields (strings as typed). */
export interface WebFormFields {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  serviceAddress: string;
  requestedItemId: string;
  preferredDate: string;
  preferredTimeOfDay: ServiceRequestTimeOfDay | '';
  notes: string;
  /** Honeypot: hidden from people */
  website: string;
}

export const EMPTY_WEB_FORM: WebFormFields = {
  contactName: '', contactEmail: '', contactPhone: '', serviceAddress: '', requestedItemId: '',
  preferredDate: '', preferredTimeOfDay: '', notes: '', website: '',
};

export type WebFormField = 'contactName' | 'contactEmail' | 'contact' | 'serviceAddress' | 'notes' | 'preferredDate';

/** The backend's rules, checked before sending (empty = fine). `today` is YYYY-MM-DD. */
export function webFormProblems(form: WebFormFields, today: string): Partial<Record<WebFormField, string>> {
  const problems: Partial<Record<WebFormField, string>> = {};
  const email = form.contactEmail.trim();
  if (!form.contactName.trim()) problems.contactName = 'Your name is required';
  else if (form.contactName.trim().length > 200) problems.contactName = 'At most 200 characters';
  if (email && (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 320)) problems.contactEmail = 'Not a valid email address';
  if (!email && !form.contactPhone.trim()) problems.contact = 'Give an email address or a phone number so we can reach you';
  if (!form.serviceAddress.trim()) problems.serviceAddress = 'The address where you need the service is required';
  else if (form.serviceAddress.trim().length > 300) problems.serviceAddress = 'At most 300 characters';
  if (!form.requestedItemId && !form.notes.trim()) problems.notes = 'Choose a service or describe what you need';
  else if (form.notes.trim().length > 2000) problems.notes = 'At most 2000 characters';
  if (form.preferredDate && form.preferredDate < today) problems.preferredDate = 'The date cannot be in the past';
  return problems;
}

/** The body to send: blank fields are left out; the honeypot is always sent as typed. */
export function webFormBody(form: WebFormFields): WebFormRequestInput {
  const trimmed = (value: string) => value.trim() || undefined;
  return {
    contactName: form.contactName.trim(),
    contactEmail: trimmed(form.contactEmail),
    contactPhone: trimmed(form.contactPhone),
    serviceAddress: form.serviceAddress.trim(),
    requestedItemId: form.requestedItemId || undefined,
    preferredDates: form.preferredDate ? [form.preferredDate] : undefined,
    preferredTimeOfDay: form.preferredTimeOfDay || undefined,
    notes: trimmed(form.notes),
    website: form.website,
  };
}
